import { useState } from "react";

import {
  Avatar,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@braintrust/braintrust-ui-components";
import {
  CheckIcon,
  CloseIcon,
  DeleteTrashIcon,
  EditPencilIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { Snackbar } from "@js/components/snackbar";
import { formatDate } from "@js/utils/date";
import { DateFormats } from "@js/utils/date/types";

import {
  useRemoveRosterFreelancersMutation,
  useUpdateRosterFreelancerNoteMutation,
} from "../../api";
import type { RosterFreelancer } from "../../types";

export type RosterFreelancersTableProps = {
  rosterId: number;
  freelancers: RosterFreelancer[];
};

const COLUMN_WIDTHS = {
  freelancer: {
    flex: { xs: "1 1 auto", md: "1.3 1 260px" },
    minWidth: 0,
    width: { xs: "100%", md: "auto" },
  },
  note: {
    flex: { xs: "1 1 auto", md: "1.3 1 220px" },
    minWidth: 0,
    width: { xs: "100%", md: "auto" },
  },
  added: { flex: "0 0 6.5rem" },
  actions: { flex: "0 0 2.625rem", minWidth: 0 },
};

const HEADER_LABEL_SX = {
  color: "var(--grey-2)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  fontWeight: 600,
};

export const RosterFreelancersTable = ({
  rosterId,
  freelancers,
}: RosterFreelancersTableProps) => {
  const isMobile = useMediaQuery("md");
  const [removeRosterFreelancers] = useRemoveRosterFreelancersMutation();

  const handleRemove = async (freelancerId: number) => {
    try {
      await removeRosterFreelancers({
        id: rosterId,
        freelancer_ids: [freelancerId],
      }).unwrap();
    } catch {
      Snackbar.error("Something went wrong.");
    }
  };

  if (!freelancers.length) {
    return (
      <Typography
        component="p"
        variant="paragraph"
        sx={{ color: "var(--grey-2)", fontStyle: "italic" }}
      >
        No freelancers on this roster yet.
      </Typography>
    );
  }

  return (
    <Stack role="table" aria-label="Roster freelancers" sx={{ gap: 1 }}>
      <Stack
        role="row"
        direction="row"
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 2,
          px: 1.5,
        }}
      >
        <Typography
          role="columnheader"
          component="span"
          variant="paragraph"
          size="small"
          sx={{ ...HEADER_LABEL_SX, ...COLUMN_WIDTHS.freelancer }}
        >
          Freelancer
        </Typography>
        <Typography
          role="columnheader"
          component="span"
          variant="paragraph"
          size="small"
          sx={{ ...HEADER_LABEL_SX, ...COLUMN_WIDTHS.note }}
        >
          Note
        </Typography>
        <Typography
          role="columnheader"
          component="span"
          variant="paragraph"
          size="small"
          sx={{ ...HEADER_LABEL_SX, ...COLUMN_WIDTHS.added }}
        >
          Added
        </Typography>
        <Stack sx={COLUMN_WIDTHS.actions} />
      </Stack>
      {freelancers.map((rosterFreelancer, index) => (
        <Stack
          key={rosterFreelancer.freelancer.id}
          role="row"
          sx={{
            position: "relative",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 1.5, md: 2 },
            px: 1.5,
            py: 1.5,
            bgcolor: "var(--white)",
            borderRadius: "0.75rem",
            boxShadow: "var(--elevation-2)",
            opacity: 0,
            animation: "roster-row-in 280ms ease-out forwards",
            animationDelay: `${Math.min(index, 8) * 30}ms`,
            transition: "box-shadow 150ms ease, transform 150ms ease",
            "@media (hover: hover) and (pointer: fine)": {
              "&:hover": {
                boxShadow: "var(--elevation-4)",
                transform: "translateY(-1px)",
              },
              "&:hover .roster-row-action": {
                opacity: 1,
              },
            },
            "@keyframes roster-row-in": {
              from: {
                opacity: 0,
                transform: "translateY(4px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Stack
            role="cell"
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1.5,
              ...COLUMN_WIDTHS.freelancer,
            }}
          >
            <Avatar
              src={{
                has_avatar_set: !!rosterFreelancer.freelancer.user.avatar,
                avatar_thumbnail: rosterFreelancer.freelancer.user.avatar,
                gravatar: rosterFreelancer.freelancer.user.gravatar,
                first_name: rosterFreelancer.freelancer.user.first_name,
                last_name: rosterFreelancer.freelancer.user.last_name,
              }}
              alt={rosterFreelancer.freelancer.user.public_name}
            />
            <Stack sx={{ gap: 0.5, minWidth: 0 }}>
              <Stack
                direction="row"
                sx={{ alignItems: "baseline", gap: 0.75, minWidth: 0 }}
              >
                <Typography
                  component="span"
                  variant="paragraph"
                  fontWeight={500}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rosterFreelancer.freelancer.user.public_name}
                </Typography>
                {!!rosterFreelancer.freelancer.role && (
                  <Typography
                    component="span"
                    variant="paragraph"
                    size="small"
                    sx={{
                      color: "var(--grey-2)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    · {rosterFreelancer.freelancer.role.name}
                  </Typography>
                )}
              </Stack>
              {!!rosterFreelancer.freelancer.top_skills.length && (
                <Stack
                  direction="row"
                  sx={{ gap: 0.5, flexWrap: "wrap", alignItems: "center" }}
                >
                  {rosterFreelancer.freelancer.top_skills.map((skill) => (
                    <Chip
                      key={skill.id}
                      size="x-small"
                      variant="squared"
                      color="soft-grey"
                      label={skill.name}
                      LabelProps={{
                        size: "small",
                        sx: { fontSize: "0.6875rem", lineHeight: 1 },
                      }}
                      sx={{
                        minHeight: "1.125rem",
                        height: "1.125rem",
                        px: 0.625,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
          <Stack role="cell" sx={COLUMN_WIDTHS.note}>
            <RosterFreelancerNoteCell
              rosterId={rosterId}
              freelancerId={rosterFreelancer.freelancer.id}
              note={rosterFreelancer.note}
            />
          </Stack>
          {isMobile ? (
            <>
              <Typography
                role="cell"
                component="span"
                variant="paragraph"
                size="small"
                sx={{ color: "var(--grey-2)", width: "100%" }}
              >
                Added{" "}
                {formatDate(
                  rosterFreelancer.created,
                  DateFormats["Jan 1, 1970"],
                )}
              </Typography>
              <IconButton
                aria-label="Remove from roster"
                size="small"
                variant="transparent"
                onClick={() => handleRemove(rosterFreelancer.freelancer.id)}
                sx={{ position: "absolute", top: 8, right: 8 }}
              >
                <DeleteTrashIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Typography
                role="cell"
                component="span"
                variant="paragraph"
                size="small"
                sx={{ color: "var(--grey-2)", ...COLUMN_WIDTHS.added }}
              >
                {formatDate(
                  rosterFreelancer.created,
                  DateFormats["Jan 1, 1970"],
                )}
              </Typography>
              <Stack
                role="cell"
                sx={{ alignItems: "flex-end", ...COLUMN_WIDTHS.actions }}
              >
                <IconButton
                  aria-label="Remove from roster"
                  size="small"
                  variant="transparent"
                  className="roster-row-action"
                  onClick={() => handleRemove(rosterFreelancer.freelancer.id)}
                  sx={{
                    transition: "opacity 150ms ease",
                    "@media (hover: hover) and (pointer: fine)": {
                      opacity: 0,
                    },
                  }}
                >
                  <DeleteTrashIcon />
                </IconButton>
              </Stack>
            </>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

type RosterFreelancerNoteCellProps = {
  rosterId: number;
  freelancerId: number;
  note: string;
};

const RosterFreelancerNoteCell = ({
  rosterId,
  freelancerId,
  note,
}: RosterFreelancerNoteCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(note);
  const [updateNote, { isLoading }] = useUpdateRosterFreelancerNoteMutation();

  const handleEdit = () => {
    setValue(note);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setValue(note);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (value === note) {
      setIsEditing(false);
      return;
    }

    try {
      await updateNote({ id: rosterId, freelancerId, note: value }).unwrap();
      setIsEditing(false);
    } catch {
      Snackbar.error("Something went wrong.");
    }
  };

  if (isEditing) {
    return (
      <Stack sx={{ gap: 1 }}>
        <TextField
          autoFocus
          multiline
          fullWidth
          size="small"
          value={value}
          sx={{ textarea: { p: "0 !important" } }}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              handleCancel();
              return;
            }
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void handleSave();
            }
          }}
        />
        <Stack direction="row" sx={{ gap: 1 }}>
          <IconButton
            aria-label="Save note"
            size="x-small"
            variant="transparent"
            onClick={handleSave}
            disabled={isLoading}
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            aria-label="Cancel"
            size="x-small"
            variant="transparent"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
      onClick={handleEdit}
    >
      <Typography
        component="span"
        variant="paragraph"
        size="small"
        sx={note ? undefined : { color: "var(--grey-2)", fontStyle: "italic" }}
      >
        {note || "Add a note..."}
      </Typography>
      <IconButton
        aria-label="Edit note"
        size="x-small"
        variant="transparent"
        className="roster-row-action"
        sx={{
          flexShrink: 0,
          transition: "opacity 150ms ease",
          "@media (hover: hover) and (pointer: fine)": {
            opacity: 0,
          },
        }}
      >
        <EditPencilIcon />
      </IconButton>
    </Stack>
  );
};
